import { registerComponent } from '../../lib/bundle-registry.js';
import { VBElement } from '../../lib/vb-element.js';
import { activateTrack, markActivePlayed, nextTrack, trackItems } from '../../lib/audio-tracks.js';

/**
 * audio-player: Platform audio player web component
 *
 * Wraps a native <audio> element (light DOM) with custom controls rendered in
 * shadow DOM. The <audio> and optional <details class="track-list"> stay in
 * light DOM — if JS is unavailable, native controls and track links work.
 *
 * @attr {boolean} autoplay - Start playing on load (subject to browser autoplay policy)
 * @attr {boolean} loop - Loop single track or entire playlist
 * @attr {boolean} shuffle - Randomize playlist order
 *
 * @fires audio-player:play - Audio playback started
 * @fires audio-player:pause - Audio playback paused
 * @fires audio-player:ended - Audio playback ended
 * @fires audio-player:track-change - Track changed in playlist mode
 * @fires audio-player:src-changed - `src` property was set
 *
 * Reflects `state` (playing | paused | ended) and `muted` on the host, and sets
 * `data-state="error"` (with a visible, politely announced message) when the
 * audio fails to load.
 *
 * @example Single track
 * <audio-player>
 *   <audio controls>
 *     <source src="track.mp3" type="audio/mpeg">
 *     <p><a href="track.mp3" download>Download track</a></p>
 *   </audio>
 * </audio-player>
 *
 * @example Playlist mode
 * <audio-player>
 *   <audio controls>
 *     <source src="tracks/01.mp3" type="audio/mpeg">
 *   </audio>
 *   <details>
 *     <summary>Track Listing</summary>
 *     <ol class="track-list">
 *       <li data-audio-active>
 *         <a href="tracks/01.mp3">01. Opening</a>
 *         <span class="track-meta"><time datetime="PT2M14S">2:14</time></span>
 *       </li>
 *     </ol>
 *   </details>
 * </audio-player>
 */

/** Real key targets that keep their native keyboard behaviour. */
const INTERACTIVE = 'input, button, a[href], summary, select, textarea, audio, [contenteditable]:not([contenteditable="false"])'

class AudioPlayerElement extends VBElement {

  static get observedAttributes() {
    return ['loop', 'shuffle']
  }

  // ─── State ─────────────────────────────────────────────────────────────────

  /** @type {HTMLAudioElement} */
  #audio = /** @type {*} */ (null)
  /** @type {Element | null} */
  #trackList = null
  #playing = false
  /** @type {Element} */
  #controls = /** @type {*} */ (null)
  /** @type {HTMLButtonElement} */
  #playBtn = /** @type {*} */ (null)
  /** @type {HTMLInputElement} */
  #timeline = /** @type {*} */ (null)
  /** @type {HTMLElement} */
  #timelineFill = /** @type {*} */ (null)
  /** @type {HTMLInputElement} */
  #volumeSlider = /** @type {*} */ (null)
  /** @type {HTMLElement} */
  #currentTimeEl = /** @type {*} */ (null)
  /** @type {HTMLElement} */
  #durationEl = /** @type {*} */ (null)
  /** @type {HTMLElement} */
  #trackTitleEl = /** @type {*} */ (null)
  /** @type {HTMLButtonElement} */
  #muteBtn = /** @type {*} */ (null)
  /** @type {HTMLElement} */
  #errorEl = /** @type {*} */ (null)
  /** True while the user drags the seek slider (mirrors :state(scrub-active)) */
  #scrubbing = false
  /** Tracks already played in the current shuffle cycle */
  #shufflePlayed = new Set()
  /** @type {(() => void) | null} */
  #onThemeChange = null
  /** True while this component (not the author) owns audio.loop */
  #ownsLoop = false

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  setup() {
    this.#audio = /** @type {HTMLAudioElement} */ (this.querySelector('audio'))
    if (!this.#audio) return false

    this.#trackList = this.querySelector('.track-list')

    // Suppress native controls — we provide our own
    this.#audio.removeAttribute('controls')

    const firstConnect = !this.shadowRoot
    this.#buildShadow()
    if (firstConnect) {
      this.#attachAudioEvents()
      this.#attachControlEvents()
      this.#attachTrackListEvents()
    }
    this.#updateTrackTitle()
    this.#syncLoop()
    this.#syncMuted()

    // The load may already have failed before the component upgraded
    const a = this.#audio
    if (a.error || (a.networkState === HTMLMediaElement.NETWORK_NO_SOURCE
      && (a.hasAttribute('src') || a.querySelector('source')))) {
      this.#showError()
    }
    if (a.readyState >= HTMLMediaElement.HAVE_METADATA) {
      this.#updateDuration()
      this.#handleTimeUpdate()
    }

    if (this.hasAttribute('autoplay')) {
      this.#audio.play().catch(() => {})
    }

    // Force style recalculation when theme changes (shadow DOM may not re-resolve custom properties)
    this.#onThemeChange = () => {
      const player = /** @type {HTMLElement | null} */ (this.shadowRoot?.querySelector('.player'))
      if (player) {
        player.style.display = 'none'
        player.offsetHeight
        player.style.display = ''
      }
    }
    this.listen(window, 'vb:theme-change', this.#onThemeChange)
    return true;
  }

  teardown() {
    // Restore native controls if component is removed
    if (this.#audio) {
      this.#audio.setAttribute('controls', '')
    }
  }

  attributeChangedCallback(name) {
    if (name === 'loop') this.#syncLoop()
    // Toggling shuffle starts a fresh shuffle cycle
    else if (name === 'shuffle') this.#shufflePlayed.clear()
  }

  /**
   * `loop` on a single track (no track list) maps onto the native audio.loop;
   * in playlist mode it is handled by #playNext wrapping to the first track.
   * Only clears audio.loop if this component set it, so an author's own
   * <audio loop> is left alone.
   */
  #syncLoop() {
    if (!this.#audio) return
    if (this.hasAttribute('loop') && !this.#trackList) {
      this.#audio.loop = true
      this.#ownsLoop = true
    } else if (this.#ownsLoop) {
      this.#audio.loop = false
      this.#ownsLoop = false
    }
  }

  // ── Data API (HTML-first / JS-first dual contract) ──────────────

  /**
   * The audio source URL (mirror of the inner <audio src> attribute).
   *
   * @returns {string} The source URL, or '' when none.
   */
  get src() {
    return this.#audio?.getAttribute('src') || this.#audio?.currentSrc || '';
  }

  /**
   * Set a new audio source. Loads the audio (browser handles `load()`).
   * Emits audio-player:src-changed { src, source: 'property' } for
   * reactive consumers.
   */
  set src(value) {
    if (!this.#audio) return;
    const next = value == null ? '' : String(value);
    if (this.#audio.getAttribute('src') === next) return;
    this.#audio.setAttribute('src', next);
    this.#audio.load?.();
    this.#syncActiveToSrc();
    this.#updateTrackTitle();
    this.dispatchEvent(new CustomEvent('audio-player:src-changed', {
      detail: { src: next, source: 'property' },
      bubbles: true,
    }));
  }

  /**
   * Current playback position in seconds.
   *
   * @returns {number} Playback position in seconds.
   */
  get currentTime() { return this.#audio?.currentTime ?? 0; }
  set currentTime(value) {
    if (this.#audio) this.#audio.currentTime = Number(value) || 0;
  }

  // ─── Shadow DOM ────────────────────────────────────────────────────────────

  #buildShadow() {
    if (this.shadowRoot) return
    const shadow = this.attachShadow({ mode: 'open' })
    shadow.innerHTML = `
      <style>${this.#styles()}</style>
      <div part="player" class="player">
        <div part="controls" class="controls">
          <button part="play-button" class="play-btn" type="button" aria-label="Play">
            <svg class="icon-play" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width="20" height="20">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <svg class="icon-pause" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width="20" height="20">
              <path d="M6 19h4V5H6zm8-14v14h4V5z"/>
            </svg>
          </button>
          <div class="timeline-group">
            <div part="track-info" class="track-info">
              <span class="track-title"></span>
              <span class="time-display">
                <time class="current-time">0:00</time>
                <span class="time-sep"> / </span>
                <time class="duration">0:00</time>
              </span>
            </div>
            <div class="timeline-wrap">
              <input part="timeline" type="range" class="timeline"
                     min="0" max="100" value="0" step="0.1"
                     aria-label="Seek" aria-valuetext="0:00 of 0:00">
              <div class="timeline-fill"></div>
            </div>
          </div>
          <div class="volume-wrap">
            <button class="mute-btn" type="button" aria-label="Mute" aria-pressed="false">
              <svg class="icon-vol" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width="16" height="16">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
              </svg>
              <svg class="icon-muted" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width="16" height="16">
                <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z"/>
              </svg>
            </button>
            <input part="volume" type="range" class="volume"
                   min="0" max="1" value="1" step="0.01"
                   aria-label="Volume">
          </div>
        </div>
        <p part="error" class="error" role="status" hidden></p>
        <slot></slot>
      </div>
    `

    this.#controls = /** @type {Element} */ (shadow.querySelector('.controls'))
    this.#playBtn = /** @type {HTMLButtonElement} */ (shadow.querySelector('.play-btn'))
    this.#timeline = /** @type {HTMLInputElement} */ (shadow.querySelector('.timeline'))
    this.#timelineFill = /** @type {HTMLElement} */ (shadow.querySelector('.timeline-fill'))
    this.#volumeSlider = /** @type {HTMLInputElement} */ (shadow.querySelector('.volume'))
    this.#currentTimeEl = /** @type {HTMLElement} */ (shadow.querySelector('.current-time'))
    this.#durationEl = /** @type {HTMLElement} */ (shadow.querySelector('.duration'))
    this.#trackTitleEl = /** @type {HTMLElement} */ (shadow.querySelector('.track-title'))
    this.#muteBtn = /** @type {HTMLButtonElement} */ (shadow.querySelector('.mute-btn'))
    this.#errorEl = /** @type {HTMLElement} */ (shadow.querySelector('.error'))
  }

  // ─── Styles ────────────────────────────────────────────────────────────────

  #styles() {
    return /* css */`
      :host {
        display: block;
        --_accent: var(--audio-player-accent, var(--color-primary, oklch(55% 0.2 260)));
        --_bg: var(--audio-player-bg, var(--color-surface, #fff));
        --_radius: var(--audio-player-radius, var(--radius-m, 0.5rem));
        --_text: var(--audio-player-text, var(--color-text, inherit));
        --_border: var(--audio-player-border, var(--color-border, #ddd));
        --_shadow: var(--audio-player-shadow, none);
        --_padding: var(--audio-player-padding, var(--size-xs, 0.5rem) var(--size-s, 0.75rem));
      }

      .player {
        background: var(--_bg);
        color: var(--_text);
        border: var(--border-width-thin, 1px) solid var(--_border);
        border-radius: var(--_radius);
        box-shadow: var(--_shadow);
        overflow: hidden;
      }

      .controls {
        display: flex;
        align-items: center;
        gap: var(--size-xs, 0.5rem);
        padding: var(--_padding);
      }

      /* ── Play button ──────────────────────────── */
      .play-btn {
        all: unset;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        border-radius: var(--radius-full, 50%);
        background: var(--_accent);
        color: var(--color-text-on-primary, white);
        cursor: pointer;
        flex-shrink: 0;
        transition: background-color var(--duration-fast, 100ms) var(--ease-default, ease),
                    transform var(--duration-fast, 100ms) var(--ease-default, ease);
      }

      .play-btn:hover {
        background-color: var(--color-primary-hover, var(--_accent));
        transform: scale(1.05);
      }

      .play-btn:active {
        transform: scale(0.97);
      }

      .play-btn:focus-visible {
        outline: var(--focus-ring-width, 2px) solid var(--color-focus-ring, var(--_accent));
        outline-offset: 2px;
      }

      .icon-pause { display: none; }
      :host([state="playing"]) .icon-play { display: none; }
      :host([state="playing"]) .icon-pause { display: block; }

      /* ── Timeline group ───────────────────────── */
      .timeline-group {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: var(--size-3xs, 2px);
      }

      .track-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--size-xs, 0.5rem);
        font-size: var(--font-size-xs, 0.75rem);
        line-height: 1.2;
      }

      .track-title {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        flex: 1;
        font-weight: 500;
      }

      .time-display {
        flex-shrink: 0;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-variant-numeric: tabular-nums;
        color: var(--color-text-muted, #666);
      }

      .time-sep { opacity: 0.5; }

      .timeline-wrap {
        position: relative;
        height: 1.25rem;
        display: flex;
        align-items: center;
      }

      .timeline-fill {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        height: 4px;
        background: var(--_accent);
        pointer-events: none;
        border-radius: var(--radius-full, 2px);
        width: 0%;
        transition: width var(--duration-instant, 50ms) linear;
      }

      .timeline {
        width: 100%;
        height: 1.25rem;
        appearance: none;
        background: transparent;
        cursor: pointer;
        position: relative;
        z-index: 1;
      }

      .timeline::-webkit-slider-runnable-track {
        height: 4px;
        background: var(--_border);
        border-radius: var(--radius-full, 2px);
      }

      .timeline::-moz-range-track {
        height: 4px;
        background: var(--_border);
        border-radius: var(--radius-full, 2px);
      }

      .timeline::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 12px;
        height: 12px;
        border-radius: var(--radius-full, 50%);
        background: var(--_accent);
        border: var(--border-width-medium, 2px) solid var(--_bg);
        box-shadow: var(--shadow-s, 0 1px 3px oklch(0% 0 0 / 0.2));
        cursor: pointer;
        margin-top: -4px;
        transition: transform var(--duration-fast, 100ms) var(--ease-default, ease);
      }

      .timeline:hover::-webkit-slider-thumb {
        transform: scale(1.2);
      }

      .timeline::-moz-range-thumb {
        width: 12px;
        height: 12px;
        border-radius: var(--radius-full, 50%);
        background: var(--_accent);
        border: var(--border-width-medium, 2px) solid var(--_bg);
        box-shadow: var(--shadow-s, 0 1px 3px oklch(0% 0 0 / 0.2));
        cursor: pointer;
      }

      .timeline::-moz-range-progress {
        height: 4px;
        background: var(--_accent);
        border-radius: var(--radius-full, 2px);
      }

      .timeline:focus-visible {
        outline: var(--focus-ring-width, 2px) solid var(--color-focus-ring, var(--_accent));
        outline-offset: 4px;
        border-radius: var(--radius-s, 2px);
      }

      /* ── Volume ───────────────────────────────── */
      .volume-wrap {
        display: flex;
        align-items: center;
        gap: var(--size-2xs, 4px);
        flex: 0 0 80px;
        min-width: 0;
      }

      .mute-btn {
        all: unset;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: var(--color-text-muted, #666);
        flex-shrink: 0;
        transition: color var(--duration-fast, 100ms) var(--ease-default, ease);
      }

      .mute-btn:hover {
        color: var(--color-text, inherit);
      }

      .mute-btn:focus-visible {
        outline: var(--focus-ring-width, 2px) solid var(--color-focus-ring, var(--_accent));
        outline-offset: 2px;
        border-radius: var(--radius-s, 2px);
      }

      .icon-muted { display: none; }
      :host([muted]) .icon-vol { display: none; }
      :host([muted]) .icon-muted { display: block; }

      .volume {
        flex: 1;
        min-width: 0;
        height: 1rem;
        appearance: none;
        background: transparent;
        cursor: pointer;
      }

      .volume::-webkit-slider-runnable-track {
        height: 3px;
        background: linear-gradient(to right,
          var(--_accent) calc(var(--_vol, 1) * 100%),
          var(--_border) calc(var(--_vol, 1) * 100%));
        border-radius: var(--radius-full, 2px);
      }

      .volume::-moz-range-track {
        height: 3px;
        background: var(--_border);
        border-radius: var(--radius-full, 2px);
      }

      .volume::-moz-range-progress {
        height: 3px;
        background: var(--_accent);
        border-radius: var(--radius-full, 2px);
      }

      .volume::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 10px;
        height: 10px;
        border-radius: var(--radius-full, 50%);
        background: var(--_accent);
        border: none;
        cursor: pointer;
        margin-top: -3.5px;
        transition: transform var(--duration-fast, 100ms) var(--ease-default, ease);
      }

      .volume:hover::-webkit-slider-thumb {
        transform: scale(1.3);
      }

      .volume::-moz-range-thumb {
        width: 10px;
        height: 10px;
        border-radius: var(--radius-full, 50%);
        background: var(--_accent);
        border: none;
        cursor: pointer;
      }

      .volume:focus-visible {
        outline: var(--focus-ring-width, 2px) solid var(--color-focus-ring, var(--_accent));
        outline-offset: 2px;
        border-radius: var(--radius-s, 2px);
      }

      /* ── Load error message ───────────────────── */
      .error {
        margin: 0;
        padding: var(--size-2xs, 4px) var(--size-s, 0.75rem) var(--size-xs, 0.5rem);
        font-size: var(--font-size-xs, 0.75rem);
        color: var(--color-error-text, var(--color-error, #b00020));
      }
      .error[hidden] { display: none; }

      /* ── Slot: hide native audio controls ─────── */
      ::slotted(audio) {
        display: none !important;
      }

      /* ── Reduced motion ───────────────────────── */
      @media (prefers-reduced-motion: reduce) {
        .play-btn,
        .timeline-fill,
        .timeline::-webkit-slider-thumb,
        .volume::-webkit-slider-thumb,
        .mute-btn {
          transition: none;
        }
        .play-btn:hover,
        .play-btn:active,
        .timeline:hover::-webkit-slider-thumb,
        .volume:hover::-webkit-slider-thumb {
          transform: none;
        }
      }
    `
  }

  // ─── Audio events ──────────────────────────────────────────────────────────

  #attachAudioEvents() {
    this.#audio.addEventListener('timeupdate', () => this.#handleTimeUpdate())
    this.#audio.addEventListener('loadedmetadata', () => {
      this.#clearError()
      this.#updateDuration()
      this.#handleTimeUpdate()
    })
    // A new source was requested: reset the time display until its metadata loads
    this.#audio.addEventListener('emptied', () => {
      this.#updateDuration()
      this.#handleTimeUpdate()
    })
    this.#audio.addEventListener('volumechange', () => this.#syncMuted())
    // `error` does not bubble. With <source> children it fires on each failed
    // <source> (not the <audio>), so listen in the capture phase and only treat
    // the last <source> failing as a load error.
    this.#audio.addEventListener('error', (e) => {
      const target = /** @type {Element} */ (e.target)
      if (target !== this.#audio) {
        if (target.localName !== 'source' || this.#audio.hasAttribute('src')) return
        let next = target.nextElementSibling
        while (next && next.localName !== 'source') next = next.nextElementSibling
        if (next) return
      }
      this.#showError()
    }, true)
    this.#audio.addEventListener('play', () => {
      this.#playing = true
      this.setAttribute('state', 'playing')
      this.#playBtn.setAttribute('aria-label', 'Pause')
      this.#emit('audio-player:play', {
        currentTime: this.#audio.currentTime,
        src: this.#audio.currentSrc
      })
    })
    this.#audio.addEventListener('pause', () => {
      this.#playing = false
      this.setAttribute('state', 'paused')
      this.#playBtn.setAttribute('aria-label', 'Play')
      this.#emit('audio-player:pause', {
        currentTime: this.#audio.currentTime
      })
    })
    this.#audio.addEventListener('ended', () => {
      this.#playing = false
      this.setAttribute('state', 'ended')
      this.#playBtn.setAttribute('aria-label', 'Play')

      // Mark track as played (it stays data-audio-active) and auto-advance
      if (this.#trackList) {
        markActivePlayed(this.#trackList)
        this.#playNext()
      }

      this.#emit('audio-player:ended', { src: this.#audio.currentSrc })
    })
  }

  /** Visible, politely announced (role=status) load-failure message. */
  #showError() {
    this.setAttribute('data-state', 'error')
    this.#errorEl.textContent = `Audio could not be loaded${this.#trackTitleEl.textContent ? `: ${this.#trackTitleEl.textContent}` : ''}.`
    this.#errorEl.hidden = false
  }

  #clearError() {
    if (this.getAttribute('data-state') === 'error') this.removeAttribute('data-state')
    this.#errorEl.hidden = true
    this.#errorEl.textContent = ''
  }

  /** Mirror audio.muted onto the host attribute, the mute button and the volume track. */
  #syncMuted() {
    if (!this.#audio || !this.#muteBtn) return
    const muted = this.#audio.muted
    if (muted) this.setAttribute('muted', '')
    else this.removeAttribute('muted')
    this.#muteBtn.setAttribute('aria-pressed', String(muted))
    this.#volumeSlider.style.setProperty('--_vol', muted ? '0' : this.#volumeSlider.value)
  }

  // ─── Control events ────────────────────────────────────────────────────────

  #attachControlEvents() {
    // Play/pause
    this.#playBtn.addEventListener('click', () => {
      if (this.#playing) {
        this.#audio.pause()
      } else {
        this.#audio.play().catch(() => {})
      }
    })

    // Seek
    this.#timeline.addEventListener('input', () => {
      const pct = Number(this.#timeline.value)
      this.#timelineFill.style.width = `${pct}%`
      if (!this.#audio.duration) return
      this.#audio.currentTime = (pct / 100) * this.#audio.duration
      this.#updateValueText(this.#audio.currentTime, this.#audio.duration)
    })

    // Internal :state(scrub-active) while the user is dragging the timeline.
    // While set, timeupdate leaves the slider value alone so it doesn't fight
    // the pointer (the time readout keeps updating).
    this.#timeline.addEventListener('pointerdown', () => {
      this.#scrubbing = true
      this.setState('scrub-active', true)
    })
    const endScrub = () => {
      if (!this.#scrubbing) return
      this.#scrubbing = false
      this.setState('scrub-active', false)
    }
    this.#timeline.addEventListener('pointerup', endScrub)
    this.#timeline.addEventListener('pointercancel', endScrub)
    this.#timeline.addEventListener('change', endScrub)

    // Volume
    this.#volumeSlider.addEventListener('input', () => {
      this.#audio.volume = Number(this.#volumeSlider.value)
      this.#audio.muted = false
      this.#syncMuted()
    })

    // Mute toggle (label stays "Mute"; aria-pressed carries the state)
    this.#muteBtn.addEventListener('click', () => {
      this.#audio.muted = !this.#audio.muted
      this.#syncMuted()
    })

    // Keyboard shortcuts. Events from shadow controls are retargeted to the
    // host, so e.target is useless here: walk the composed path up to the host
    // and bail if the real target sits inside an interactive control (volume
    // or seek slider, mute/play button, playlist <summary>, track links...).
    // Those keep their native key behaviour; shortcuts only fire when focus is
    // on the host itself or a non-interactive part.
    this.addEventListener('keydown', (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return
      if (this.#fromInteractive(e)) return

      switch (e.key) {
        case ' ':
          e.preventDefault()
          if (this.#playing) this.#audio.pause()
          else this.#audio.play().catch(() => {})
          break
        case 'ArrowLeft':
          e.preventDefault()
          this.#audio.currentTime = Math.max(0, this.#audio.currentTime - 10)
          break
        case 'ArrowRight':
          e.preventDefault()
          this.#audio.currentTime = Math.min(
            this.#audio.duration || 0,
            this.#audio.currentTime + 10
          )
          break
        case 'm':
        case 'M':
          this.#audio.muted = !this.#audio.muted
          this.#syncMuted()
          break
      }
    })

    // The host is the focus target for the shortcuts above (they never fire
    // from inside a control, so dropping the tab stop would make them
    // unreachable). Give that tab stop a role and name so it isn't announced
    // as an anonymous focusable element.
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0')
    if (!this.hasAttribute('role')) this.setAttribute('role', 'group')
    if (!this.hasAttribute('aria-label') && !this.hasAttribute('aria-labelledby')) {
      this.setAttribute('aria-label', 'Audio player')
    }
    if (!this.hasAttribute('aria-keyshortcuts')) {
      this.setAttribute('aria-keyshortcuts', 'Space ArrowLeft ArrowRight M')
    }
  }

  /**
   * True when a key event originated inside an interactive control that lives
   * within this player (light or shadow DOM), up to but excluding the host.
   * @param {Event} e
   */
  #fromInteractive(e) {
    for (const node of e.composedPath()) {
      if (node === this) return false
      if (node instanceof Element && node.matches(INTERACTIVE)) return true
    }
    return false
  }

  // ─── Track list events ─────────────────────────────────────────────────────

  #attachTrackListEvents() {
    if (!this.#trackList) return

    this.#trackList.addEventListener('click', (e) => {
      const target = /** @type {Element | null} */ (e.target)
      const link = /** @type {HTMLAnchorElement | null} */ (target?.closest('a[href]'))
      const li = link?.closest('li')
      if (!link || !li) return
      e.preventDefault()

      this.#loadTrack(li)
    })
  }

  /** @param {HTMLLIElement} li */
  #loadTrack(li) {
    if (!this.#trackList) return
    const track = activateTrack(this.#audio, this.#trackList, li)
    if (!track) return
    this.#shufflePlayed.add(li)
    this.#updateTrackTitle()

    this.#emit('audio-player:track-change', track)
  }

  /**
   * After `src` is set by property, move data-audio-active to the track whose
   * link matches the new source (or clear it when none does), so the title and
   * the highlighted track describe what is actually loaded.
   */
  #syncActiveToSrc() {
    if (!this.#trackList) return
    const src = this.#audio.src
    const match = trackItems(this.#trackList)
      .find((li) => /** @type {HTMLAnchorElement} */ (li.querySelector('a[href]')).href === src)
    this.#trackList.querySelectorAll('li[data-audio-active]').forEach((li) => {
      if (li !== match) li.removeAttribute('data-audio-active')
    })
    match?.setAttribute('data-audio-active', '')
  }

  #playNext() {
    if (!this.#trackList) return
    const loop = this.hasAttribute('loop')

    if (!this.hasAttribute('shuffle')) {
      const next = nextTrack(this.#trackList, { loop })
      if (next) this.#loadTrack(next)
      return
    }

    // Shuffle: each track plays once per cycle, never the same one twice in a
    // row. At the end of a cycle, `loop` starts a fresh cycle; otherwise stop.
    const items = trackItems(this.#trackList)
    const current = /** @type {HTMLLIElement | null} */ (this.#trackList.querySelector('li[data-audio-active]'))
    if (current) this.#shufflePlayed.add(current)
    let pool = items.filter((li) => !this.#shufflePlayed.has(li))
    if (!pool.length) {
      this.#shufflePlayed.clear()
      if (!loop) return
      pool = items.length > 1 ? items.filter((li) => li !== current) : items
    }
    const next = pool[Math.floor(Math.random() * pool.length)]
    if (next) this.#loadTrack(next)
  }

  // ─── Time display ──────────────────────────────────────────────────────────

  #handleTimeUpdate() {
    const t = this.#audio.currentTime
    const d = this.#audio.duration || 0
    const pct = d ? (t / d) * 100 : 0

    this.#currentTimeEl.textContent = this.#formatTime(t)
    this.#updateValueText(t, d)
    // Leave the slider alone while the user is dragging it
    if (this.#scrubbing) return
    this.#timeline.value = String(pct)
    this.#timelineFill.style.width = `${pct}%`
  }

  /** Spoken value for the seek slider, e.g. "1:05 of 3:20". */
  #updateValueText(t, d) {
    this.#timeline.setAttribute('aria-valuetext', `${this.#formatTime(t)} of ${this.#formatTime(d)}`)
  }

  #updateDuration() {
    const d = this.#audio.duration || 0
    this.#durationEl.textContent = this.#formatTime(d)
  }

  #updateTrackTitle() {
    if (!this.#trackTitleEl) return

    // Try to get title from active track
    if (this.#trackList) {
      const active = this.#trackList.querySelector('li[data-audio-active] a')
      if (active) {
        this.#trackTitleEl.textContent = active.textContent
        return
      }
    }

    // Fall back to filename from src (the attribute first: currentSrc lags
    // behind a just-set src until the new resource is selected)
    const src = this.#audio.src || this.#audio.currentSrc || this.#audio.querySelector('source')?.src || ''
    const file = src ? /** @type {string} */ (src.split('/').pop()).split(/[?#]/)[0] : ''
    this.#trackTitleEl.textContent = file.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  #formatTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00'
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  #emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail
    }))
  }
}

registerComponent('audio-player', AudioPlayerElement)

export { AudioPlayerElement }
